import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Button from '../../Button'
import Spinner from '../../Spinner'
import ControlLabel from '../../FormControls/ControlLabel'
import Checkbox from '../../FormControls/Checkbox'
import Container from '../../Grid/Container'
import styles from './category-selector.module.scss'
import WithGroup from './subcomponents/withGroup'
import WithoutGroup from './subcomponents/withoutGroup'
import { Grid } from '../../index'

const CategorySelector = ({
  values = [],
  groups = [],
  onSelect = () => {},
  onReset = null,
  loading = false,
  className = '',
  i18n = {
    listTitle: undefined,
    dropSelection: 'Drop selection',
    apply: 'Apply',
  },
  OptGroupTemplate = ({ id, checked, label, onChange }) => (
    <ControlLabel
      id={'group-' + id}
      key={'group-' + id}
      name={'group-' + id}
      control={<Checkbox />}
      checked={checked}
      label={label}
      onChange={onChange}
    />
  ),
  RowTemplate = ({ id, checked, label, onChange }) => (
    <ControlLabel
      id={'account-' + id}
      key={'account-' + id}
      name={'account-' + id}
      control={<Checkbox />}
      checked={checked}
      label={label}
      onChange={onChange}
    />
  ),
  EmptyRowTemplate = ({ label }) => (
    <ControlLabel control={null} label={label} />
  ),
}) => {
  const [stateGroups, setStateGroups] = useState(groups)
  const [openedGroups, setOpenedGroups] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [selectedOptions, setSelectedOptions] = useState(values)

  useEffect(() => {
    setSelectedOptions(values)
  }, [values])

  useEffect(() => {
    setStateGroups(groups)
  }, [groups])

  const handleGroupCheckboxChange = useCallback(
    (groupName) => {
      const optionsIds = stateGroups
        .find(({ label }) => label === groupName)
        .options.map(({ id }) => id)

      if (selectedGroups.includes(groupName)) {
        setSelectedOptions(
          selectedOptions.filter((option) => !optionsIds.includes(option)),
        )
      } else {
        setSelectedOptions(selectedOptions.concat(optionsIds))
      }
    },
    [selectedOptions, stateGroups, selectedGroups],
  )

  const handleCheckboxChange = useCallback(
    (id) => {
      if (selectedOptions.includes(id)) {
        setSelectedOptions(selectedOptions.filter((option) => option !== id))
      } else {
        setSelectedOptions(selectedOptions.concat([id]))
      }
    },
    [selectedOptions],
  )

  const toggleOptGroup = useCallback(
    (groupName) => {
      if (openedGroups.includes(groupName)) {
        setOpenedGroups(openedGroups.filter((group) => group !== groupName))
      } else {
        setOpenedGroups(openedGroups.concat(groupName))
      }
    },
    [openedGroups],
  )

  const applyIds = useCallback(() => {
    onSelect(selectedOptions)
  }, [selectedOptions])

  const dropSelection = useCallback(() => {
    setSelectedOptions([])
  }, [])

  useEffect(() => {
    const selectedGroups = stateGroups
      .filter(
        ({ options = [] }) =>
          options.filter((option) => selectedOptions.includes(option.id))
            .length > 0,
      )
      .map(({ label }) => label)
    setSelectedGroups(selectedGroups)
  }, [selectedOptions, stateGroups])

  useEffect(() => {
    const hasCheckedOption = groups
      .filter(({ options = [] }) =>
        options.find(({ id }) => selectedOptions.includes(id)),
      )
      .map(({ label }) => label)
    setSelectedGroups(hasCheckedOption)
  }, [groups, selectedOptions])

  return (
    <div>
      {!!i18n.listTitle && (
        <div className={styles['list-title']}>{i18n.listTitle}</div>
      )}
      <div className={clsx(styles['selectors-container'], className)}>
        {loading ? (
          <Container textAlign={'center'}>
            <Spinner color={'basic-primary-blue'} />
          </Container>
        ) : (
          stateGroups.map((group) =>
            group?.options ? (
              <WithGroup
                key={'group-' + group.label}
                group={group}
                selectedGroups={selectedGroups}
                selectedOptions={selectedOptions}
                openedGroups={openedGroups}
                handleCheckboxChange={handleGroupCheckboxChange}
                handleGroupCheckboxChange={handleGroupCheckboxChange}
                EmptyRowTemplate={EmptyRowTemplate}
                toggleOptGroup={toggleOptGroup}
                OptGroupTemplate={OptGroupTemplate}
                RowTemplate={RowTemplate}
                onSelect={onSelect}
              />
            ) : (
              <WithoutGroup
                key={'group-' + group.label}
                selectedOptions={selectedOptions}
                handleCheckboxChange={handleCheckboxChange}
                EmptyRowTemplate={EmptyRowTemplate}
                OptGroupTemplate={OptGroupTemplate}
                onSelect={onSelect}
                group={group}
                {...group}
              />
            ),
          )
        )}
      </div>
      <Grid className={styles['action-buttons']} gap={16}>
        <Button
          type="button"
          disabled={loading}
          className={styles['button--decline']}
          onClick={() => (onReset ? onReset() : dropSelection())}
        >
          {i18n.dropSelection}
        </Button>
        <Button
          type="button"
          disabled={loading}
          variant="contained"
          onClick={() => applyIds()}
        >
          {i18n.apply}
        </Button>
      </Grid>
    </div>
  )
}

CategorySelector.propTypes = {
  values: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ),
  RowTemplate: PropTypes.func,
  className: PropTypes.string,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
          label: PropTypes.string,
        }),
      ),
    }),
  ),
  placeholder: PropTypes.string,
  onSelect: PropTypes.func,
  onReset: PropTypes.oneOfType([PropTypes.func, PropTypes.oneOf([null])]),
  loading: PropTypes.bool,
  i18n: PropTypes.shape({
    listTitle: PropTypes.string,
    dropSelection: PropTypes.string,
    apply: PropTypes.string,
  }),
  OptGroupTemplate: PropTypes.elementType,
  EmptyRowTemplate: PropTypes.elementType,
}

export default CategorySelector
